import { z } from 'zod';
/**
 * Relatórios (Etapa 4): consulta de atendimentos com filtros e os indicadores da operação.
 * Quem tem `relatorios.global` enxerga todos os setores; com `relatorios.setor`, só os seus.
 */
/**
 * Uma seleção do painel chega na URL como lista separada por vírgula (`setorIds=a,b`).
 * É o formato que sobrevive a copiar e colar o link, que é como um relatório circula.
 */
const lista = (item) => z.preprocess((valor) => typeof valor === 'string'
    ? valor
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean)
    : valor, z.array(item).max(200));
/** Filtros da consulta. Datas em AAAA-MM-DD (dia inteiro no fuso de São Paulo). */
export const filtroRelatorioSchema = z.object({
    de: z.iso.date().optional(),
    ate: z.iso.date().optional(),
    setorId: z.uuid().optional(),
    atendenteId: z.uuid().optional(),
    motivoTagId: z.uuid().optional(),
    /**
     * Seleção feita clicando nos gráficos (o "filtro cruzado" do BI). Convive com os campos
     * de cima: o formulário escolhe o recorte grande, o clique fatia dentro dele.
     *
     * São listas porque Ctrl+clique acumula — e porque um dia não é um intervalo: clicar em
     * 25/09 e 28/09 não pode trazer 26 e 27 no meio.
     */
    setorIds: lista(z.uuid()).optional(),
    atendenteIds: lista(z.uuid()).optional(),
    /** `sem` representa os atendimentos sem motivo registrado, que é sempre a maior fatia. */
    motivoTagIds: lista(z.union([z.uuid(), z.literal('sem')])).optional(),
    dias: lista(z.iso.date()).optional(),
    horas: lista(z.coerce.number().int().min(0).max(23)).optional(),
    status: z.enum(['bot', 'fila', 'em_atendimento', 'aguardando_cliente', 'encerrada']).optional(),
    origem: z.enum(['web', 'app']).optional(),
    /** 1 a 5; "sem" traz quem não avaliou. */
    avaliacao: z.union([z.coerce.number().int().min(1).max(5), z.literal('sem')]).optional(),
    /** Protocolo, CPF/CNPJ ou nome do cliente — o servidor descobre o que é. */
    busca: z.string().trim().max(120).optional(),
    /** Paginação por cursor (id do último item da página anterior). */
    cursor: z.string().optional(),
    limite: z.coerce.number().int().min(1).max(200).default(50),
});
/** Como o atendimento se resolveu: só bot, só pessoa, ou os dois. */
export const TipoAtendimento = {
    AUTOMATICO: 'automatico',
    MISTO: 'misto',
    HUMANO: 'humano',
};
export const ROTULO_TIPO_ATENDIMENTO = {
    automatico: 'Automático',
    misto: 'Misto',
    humano: 'Humano',
};
/** Formatos de saída da consulta. */
export const FORMATOS_EXPORTACAO = ['xlsx', 'csv'];
// ───────────────────────────── Relatórios prontos ──────────────────────────────
/**
 * Os relatórios que se baixa por tipo, cada um com as colunas que fazem sentido para
 * aquela pergunta.
 *
 * Antes existia uma planilha só, a de atendimentos, com quarenta colunas — quem queria
 * saber de pausa ou de nota do técnico tinha que garimpar. Todos respeitam os filtros da
 * tela (período, setor, atendente e a seleção dos gráficos) e o recorte de permissão de
 * quem baixa.
 */
export const TIPOS_RELATORIO = [
    'atendimentos',
    'avaliacao-atendente',
    'avaliacao-tecnico',
    'produtividade',
    'setores',
    'motivos',
    'pausas',
    'jornada',
    'transferencias',
    'fila',
];
export const RELATORIOS = [
    {
        id: 'atendimentos',
        nome: 'Atendimentos',
        descricao: 'Uma linha por protocolo, com tempos, mensagens, motivo e avaliação.',
        grupo: 'Atendimento',
    },
    {
        id: 'fila',
        nome: 'Espera e abandono',
        descricao: 'Quanto cada cliente esperou na fila e quem desistiu antes de ser atendido.',
        grupo: 'Atendimento',
    },
    {
        id: 'motivos',
        nome: 'Motivos',
        descricao: 'Volume por motivo de encerramento, com participação no total.',
        grupo: 'Atendimento',
    },
    {
        id: 'transferencias',
        nome: 'Transferências',
        descricao: 'De onde para onde cada atendimento foi, por quem e por quê.',
        grupo: 'Atendimento',
    },
    {
        id: 'avaliacao-atendente',
        nome: 'Avaliação do atendente',
        descricao: 'Nota e comentário que o cliente deixou, protocolo a protocolo.',
        grupo: 'Qualidade',
    },
    {
        id: 'avaliacao-tecnico',
        nome: 'Avaliação do técnico',
        descricao: 'Nota que o atendente deu ao técnico de campo na liberação de sinal.',
        grupo: 'Qualidade',
    },
    {
        id: 'setores',
        nome: 'Desempenho por setor',
        descricao: 'Volume, espera, duração e satisfação de cada setor.',
        grupo: 'Qualidade',
    },
    {
        id: 'produtividade',
        nome: 'Produtividade da equipe',
        descricao: 'Atendidos, encerrados, duração média, primeira resposta e CSAT por pessoa.',
        grupo: 'Equipe',
    },
    {
        id: 'pausas',
        nome: 'Pausas',
        descricao: 'Cada pausa da equipe: quem, motivo, início, fim e duração.',
        grupo: 'Equipe',
    },
    {
        id: 'jornada',
        nome: 'Entradas e saídas',
        descricao: 'Quando cada pessoa entrou e saiu do sistema, com tempo conectado.',
        grupo: 'Equipe',
    },
];
/** Segundos → "2h 05min", "3min 20s", "45s". Usado nas telas e nas exportações. */
export function formatarDuracao(segundos) {
    if (segundos === null || segundos === undefined)
        return '—';
    if (segundos < 60)
        return `${Math.round(segundos)}s`;
    const min = Math.floor(segundos / 60);
    if (min < 60)
        return `${min}min ${String(Math.round(segundos % 60)).padStart(2, '0')}s`;
    const horas = Math.floor(min / 60);
    return `${horas}h ${String(min % 60).padStart(2, '0')}min`;
}
//# sourceMappingURL=relatorios.js.map